import React, { useState, useEffect } from 'react'
import { groupBy, get } from 'lodash'
import { Position, Tabs, Tab } from '@blueprintjs/core'
import { ApolloError } from 'apollo-boost'
import { Avatar, Button, Flex, Toaster, Popover } from '@ui'
import { useReload } from '@hooks/reloadContext'
import EmployeeTab from './EmployeeTab'
import DepartmentTab from './DepartmentTab'
import NeighborhoodTab from './NeighborhoodTab'
import { pluralize, isShared } from '@utils'
import api, {
  IDepartment,
  IUsageType,
  ICatalogEmployee,
  INeighborhood,
  IFloorMapSpace,
  ISpaceAssignment,
  IFullEmployee,
} from '@api'

interface SpaceAssignmentSelectProps {
  floorId: string
  assignmentPlanId: string
  space?: IFloorMapSpace
  spaces?: IFloorMapSpace[]
  onChange: (spaces: IFloorMapSpace[]) => void
  onError?: (error: ApolloError) => void
}

let initTabId = '1'

export default function SpaceAssignmentSelect({
  floorId,
  assignmentPlanId,
  onChange,
  space,
  spaces,
  onError = () => null,
}: SpaceAssignmentSelectProps) {
  const [tabId, setTabId] = useState(initTabId)
  const reload = useReload()
  useEffect(() => {
    initTabId = tabId
  })

  function allocate(toDepartment: IDepartment | null) {
    let team: IDepartment | null = null
    let department: IDepartment | null = null

    if (toDepartment) {
      team = toDepartment.parentDepartment ? toDepartment : null
      department = toDepartment.parentDepartment ? toDepartment.parentDepartment : toDepartment
    }

    onChange(
      (space ? [space] : spaces || []).map((s) => {
        const spaceAssignment = s.spaceAssignment as ISpaceAssignment

        return {
          ...s,
          spaceAssignment: {
            ...spaceAssignment,
            // Check if employee belongs to department or team
            // space is being allocated to, if true - do not unseat employee
            employee:
              toDepartment && s.spaceAssignment
                ? get(s, 'spaceAssignment.team.id') === get(team, 'id') ||
                  get(s, 'spaceAssignment.department.it') === get(department, 'id')
                  ? get(s, 'spaceAssignment.employee', null)
                  : null
                : null,
            department,
            team,
          },
        }
      })
    )
    api.space
      .allocate({
        assignmentPlanId,
        toDepartmentId: get(toDepartment, 'id', null),
        spaceIds: space ? [space.id] : spaces?.map(({ id }) => id) || [],
      })
      .then((spaces: IFloorMapSpace[]) => {
        reload.employeeCatalog()
        Toaster.success({
          message: `${spaces.length} ${pluralize(spaces.length, 'space')} ${
            toDepartment ? `allocated to ${toDepartment.name}` : 'unallocated'
          }`,
        })
      })
      .catch((error: ApolloError) => {
        onError(error)
        Toaster.fail({ error })
      })
  }

  function seat({
    toSpaceId,
    fromSpaceId,
    employee,
  }: {
    toSpaceId: string
    fromSpaceId?: string
    employee: ICatalogEmployee
  }) {
    if (!space) return
    const shared = isShared(space.usageType)

    onChange([
      {
        ...space,
        spaceAssignment: {
          ...space.spaceAssignment,
          employee: shared ? null : employee,
          sharedEmployees: shared
            ? [...(get(space, 'spaceAssignment.sharedEmployees') || []), employee]
            : null,
          department: employee.dept,
          team: employee.team,
          neighborhood: null,
        },
      },
    ])

    api.space
      .seat({
        assignmentPlanId,
        toSpaceId,
        fromSpaceId,
        employeeId: employee.id,
        moveType: 'move',
      })
      .then(() => {
        reload.employeeCatalog()
        Toaster.success({
          message: `Moved ${employee.name} to a ${space.code}`,
        })
      })
      .catch((error: ApolloError) => {
        onError(error)
        Toaster.fail({ error })
      })
  }

  function unseat(employee: IFullEmployee) {
    if (!space || !employee) return
    const shared = isShared(space.usageType)
    const spaceAssignment = space.spaceAssignment as ISpaceAssignment

    onChange([
      {
        ...space,
        spaceAssignment: {
          ...spaceAssignment,
          employee: null,
          sharedEmployees: shared
            ? (spaceAssignment.sharedEmployees || []).filter(({ id }) => id !== employee.id)
            : null,
        },
      },
    ])

    api.space
      .seat({
        assignmentPlanId,
        fromSpaceId: space.id,
        toSpaceId: null,
        employeeId: employee.id,
        moveType: 'move',
      })
      .then(() => {
        reload.employeeCatalog()
        Toaster.success({
          message: `${employee.name} was removed from ${space.code}`,
        })
      })
      .catch((error: ApolloError) => {
        onError(error)
        Toaster.fail({ error })
      })
  }

  function unseatAll() {
    if (!space || !isShared(space.usageType)) return
    const spaceAssignment = space.spaceAssignment as ISpaceAssignment
    const sharedEmployees = spaceAssignment.sharedEmployees as IFullEmployee[]

    onChange([
      {
        ...space,
        spaceAssignment: {
          ...spaceAssignment,
          sharedEmployees: null,
        },
      },
    ])

    Promise.all(
      sharedEmployees.map((e) =>
        api.space.seat({
          assignmentPlanId,
          fromSpaceId: space.id,
          toSpaceId: null,
          employeeId: e.id,
          moveType: 'move',
        })
      )
    )
      .then(() => {
        reload.employeeCatalog()
        Toaster.success({
          message:
            sharedEmployees.length === 1
              ? `${sharedEmployees[0].name} was removed from ${space.code}`
              : `${sharedEmployees.length} employees were removed from ${space.code}`,
        })
      })
      .catch((error: ApolloError) => {
        onError(error)
        Toaster.fail({ error })
      })
  }

  function updateSpaceUsage(usageType: IUsageType) {
    const nextSpaces = (space ? [space] : spaces || []).map((s) => {
      const spaceAssignment = s.spaceAssignment as ISpaceAssignment
      return {
        ...s,
        usageType,
        spaceAssignment: {
          ...spaceAssignment,
          employee: null,
          neighborhood: null,
        },
      }
    })

    if (nextSpaces.length) {
      onChange(nextSpaces)

      api.space
        .batchUpdateUsageTypes(
          nextSpaces.map(({ id }) => id),
          usageType.name
        )
        .then(() => {
          reload.neighborhoods()
          Toaster.success({
            message:
              nextSpaces.length === 1
                ? `${nextSpaces[0].code} changed to ${usageType.displayName}`
                : `${nextSpaces.length} spaces changed to ${usageType.displayName}`,
          })
        })
        .catch((error: ApolloError) => {
          onError(error)
          Toaster.fail({ error })
        })
    }
  }

  function addToNeighborhood(neighborhood: INeighborhood): Promise<void> | undefined {
    const nextSpaces = (space ? [space] : spaces || []).map((s) => {
      const spaceAssignment = s.spaceAssignment as ISpaceAssignment
      return {
        ...s,
        spaceAssignment: {
          ...spaceAssignment,
          neighborhood,
        },
      }
    })

    if (nextSpaces.length) {
      onChange(nextSpaces)

      return api.neighborhood
        .addSpaces(
          neighborhood.id,
          nextSpaces.map(({ id }) => id)
        )
        .then(() => {
          reload.neighborhoods()
          Toaster.success({
            message:
              nextSpaces.length === 1
                ? `${nextSpaces[0].code} added to ${neighborhood.name}`
                : `${nextSpaces.length} spaces assigned to ${neighborhood.name}`,
          })
        })
        .catch((error: ApolloError) => {
          onError(error)
          Toaster.fail({ error })
        })
    }
  }

  function removeFromNeighborhood() {
    const groups = groupBy(space ? [space] : spaces || [], 'spaceAssignment.neighborhood.id')
    const nextSpaces = (space ? [space] : spaces || []).map((s) => {
      const spaceAssignment = s.spaceAssignment as ISpaceAssignment
      return {
        ...s,
        spaceAssignment: {
          ...spaceAssignment,
          neighborhood: null,
        },
      }
    })

    if (nextSpaces.length) {
      onChange(nextSpaces)
      Promise.all(
        Object.keys(groups).map((neighborhoodId) =>
          api.neighborhood.removeSpaces(
            neighborhoodId,
            (groups[neighborhoodId] as IFloorMapSpace[]).map(({ id }) => id)
          )
        )
      )
        .then(() => {
          reload.neighborhoods()
          Toaster.success({
            message:
              nextSpaces.length === 1
                ? `${nextSpaces[0].code} removed from the neighborhood`
                : `${nextSpaces.length} spaces remove from the neighborhood`,
          })
        })
        .catch((error: ApolloError) => {
          onError(error)
          Toaster.fail({ error })
        })
    }
  }

  return (
    <Popover
      minimal
      usePortal={false}
      position={Position.BOTTOM}
      content={
        <div style={{ padding: '0 10px 10px 10px' }}>
          <Tabs
            selectedTabId={tabId}
            onChange={(newTabId: string) => setTabId(newTabId)}
            renderActiveTabPanelOnly
          >
            <Tab
              id="1"
              title="Employee"
              panel={
                <EmployeeTab
                  {...{
                    assignmentPlanId,
                    space,
                    seat,
                    unseat,
                    unseatAll,
                    updateSpaceUsage,
                  }}
                />
              }
            />
            <Tab
              id="2"
              title="Department"
              panel={<DepartmentTab space={space} spaces={spaces} allocate={allocate} />}
            />
            <Tab
              id="3"
              title="Neighborhood"
              panel={
                <NeighborhoodTab
                  {...{
                    space,
                    spaces,
                    updateSpaceUsage,
                    addToNeighborhood,
                    removeFromNeighborhood,
                    floorId,
                  }}
                />
              }
            />
          </Tabs>
        </div>
      }
    >
      <Button
        ghost
        rightIconName="chevron-down"
        text={<SpaceAssignmentEl spaceAssignment={space ? space.spaceAssignment : null} />}
      />
      <div style={{ padding: '0 10px 10px 10px' }}>
        <Tabs
          selectedTabId={tabId}
          onChange={(newTabId: string) => setTabId(newTabId)}
          renderActiveTabPanelOnly
        >
          <Tab
            id="1"
            title="Employee"
            panel={
              <EmployeeTab
                {...{
                  assignmentPlanId,
                  space,
                  seat,
                  unseat,
                  unseatAll,
                  updateSpaceUsage,
                }}
              />
            }
          />
          <Tab
            id="2"
            title="Department"
            panel={<DepartmentTab space={space} spaces={spaces} allocate={allocate} />}
          />
          <Tab
            id="3"
            title="Neighborhood"
            panel={
              <NeighborhoodTab
                {...{
                  space,
                  spaces,
                  updateSpaceUsage,
                  addToNeighborhood,
                  removeFromNeighborhood,
                  floorId,
                }}
              />
            }
          />
        </Tabs>
      </div>
    </Popover>
  )
}

function SpaceAssignmentEl({ spaceAssignment }: { spaceAssignment: ISpaceAssignment | null }) {
  const employee = get(spaceAssignment, 'employee')
  const employees = get(spaceAssignment, 'sharedEmployees') || []
  const department = get(spaceAssignment, 'department')
  const team = get(spaceAssignment, 'team')
  const neighborhood = get(spaceAssignment, 'neighborhood')

  if (employee && department) {
    return (
      <Flex align="center" style={{ position: 'relative' }}>
        <Avatar name={employee.name} assets={employee.assets} size={Avatar.Size.XS} />
        <div style={{ marginLeft: 10 }}>
          {employee.name}
          <div style={{ fontSize: 12, color: '#999' }}>
            {department.name}
            {team ? `/ ${team.name}` : ''}
          </div>
        </div>
      </Flex>
    )
  }

  if (employees.length) {
    return (
      <div>
        <div style={neighborhood ? { fontSize: 12, color: '#999' } : {}}>
          {department && (
            <div>
              {department.name}
              {team ? `/ ${team.name}` : ''}
            </div>
          )}
          <Flex style={{ marginTop: 5 }}>
            {employees.map((e) => (
              <div style={{ marginRight: 5 }} key={e.id}>
                <Avatar name={e.name} assets={e.assets} size={Avatar.Size.XS} />
              </div>
            ))}
          </Flex>
        </div>
      </div>
    )
  }

  if (department) {
    return (
      <div>
        {neighborhood && neighborhood.name}
        <div style={neighborhood ? { fontSize: 12, color: '#999' } : {}}>
          {department.name}
          {team ? `/ ${team.name}` : ''}
        </div>
      </div>
    )
  }

  if (neighborhood) {
    return <div>{neighborhood.name}</div>
  }

  return <span>Assign</span>
}
